/**
 * Multicam Time Mapping Utilities
 *
 * These utilities handle the conversion between shared timeline time and
 * individual source video times for multicam synchronization.
 *
 * Key concepts:
 * - Timeline time: The shared "master" timeline that all angles are synced to
 * - Source time: The actual playback position within a specific video file
 *
 * Formula: sourceTimeMs = timelineTimeMs - timelineStartMs + sourceInMs
 * Inverse: timelineTimeMs = sourceTimeMs - sourceInMs + timelineStartMs
 */

import { MulticamAngle } from '../../graphqlService';

/**
 * Convert shared timeline time to source video time
 *
 * @param timelineTimeMs - Position on the shared timeline (ms)
 * @param angle - The angle to convert for
 * @returns The corresponding position in the source video (ms)
 */
export const timelineToSource = (
  timelineTimeMs: number,
  angle: MulticamAngle
): number => {
  return timelineTimeMs - angle.timelineStartMs + angle.sourceInMs;
};

/**
 * Convert source video time to shared timeline time
 *
 * @param sourceTimeMs - Position in the source video (ms)
 * @param angle - The angle to convert for
 * @returns The corresponding position on the shared timeline (ms)
 */
export const sourceToTimeline = (
  sourceTimeMs: number,
  angle: MulticamAngle
): number => {
  return sourceTimeMs - angle.sourceInMs + angle.timelineStartMs;
};

/**
 * Check if an angle has footage at a specific timeline position
 *
 * @param timelineTimeMs - Position on the shared timeline (ms)
 * @param angle - The angle to check
 * @returns True if the angle has footage at this timeline position
 */
export const angleHasFootage = (
  timelineTimeMs: number,
  angle: MulticamAngle
): boolean => {
  // Convert to source time
  const sourceTime = timelineToSource(timelineTimeMs, angle);

  // Check if before source in point
  if (sourceTime < angle.sourceInMs) {
    return false;
  }

  // Check if after source out point (if defined)
  if (angle.sourceOutMs !== null && angle.sourceOutMs !== undefined && sourceTime > angle.sourceOutMs) {
    return false;
  }

  // Check if exceeds video duration
  if (sourceTime > angle.durationMs) {
    return false;
  }

  return true;
};

/**
 * Get the range of timeline time where an angle has footage
 *
 * @param angle - The angle to get the range for
 * @returns Object with start and end timeline times (ms)
 */
export const getAngleTimelineRange = (
  angle: MulticamAngle
): { start: number; end: number } => {
  // Start: when sourceIn maps to timeline
  const start = sourceToTimeline(angle.sourceInMs, angle);

  // End: when sourceOut (or duration) maps to timeline
  const sourceEnd = angle.sourceOutMs ?? angle.durationMs;
  const end = sourceToTimeline(sourceEnd, angle);

  return { start, end };
};

/**
 * Get total timeline duration based on all angles
 * This is the maximum end point across all angle timeline ranges
 *
 * @param angles - Array of angles
 * @returns Total timeline duration in milliseconds
 */
export const getTimelineDuration = (angles: MulticamAngle[]): number => {
  if (angles.length === 0) {
    return 0;
  }

  let maxEnd = 0;

  for (const angle of angles) {
    const range = getAngleTimelineRange(angle);
    if (range.end > maxEnd) {
      maxEnd = range.end;
    }
  }

  return maxEnd;
};

/**
 * Get the earliest timeline start across all angles
 *
 * @param angles - Array of angles
 * @returns Earliest timeline start in milliseconds (usually 0)
 */
export const getTimelineStart = (angles: MulticamAngle[]): number => {
  if (angles.length === 0) {
    return 0;
  }

  let minStart = Infinity;

  for (const angle of angles) {
    const range = getAngleTimelineRange(angle);
    if (range.start < minStart) {
      minStart = range.start;
    }
  }

  return minStart === Infinity ? 0 : minStart;
};

/**
 * Format milliseconds to MM:SS.ms display format
 *
 * @param ms - Time in milliseconds
 * @returns Formatted time string (e.g., "01:23.456")
 */
export const formatTimeMs = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = Math.floor(ms % 1000);

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
};

/**
 * Format milliseconds to MM:SS display format (no ms)
 *
 * @param ms - Time in milliseconds
 * @returns Formatted time string (e.g., "01:23")
 */
export const formatTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Parse a time string to milliseconds
 * Supports formats: "MM:SS", "MM:SS.ms", "SS", "SS.ms"
 *
 * @param timeStr - Time string to parse
 * @returns Time in milliseconds, or NaN if invalid
 */
export const parseTimeToMs = (timeStr: string): number => {
  const parts = timeStr.split(':');

  if (parts.length === 2) {
    // MM:SS or MM:SS.ms format
    const minutes = parseInt(parts[0], 10);
    const secondsParts = parts[1].split('.');
    const seconds = parseInt(secondsParts[0], 10);
    const ms = secondsParts[1] ? parseInt(secondsParts[1].padEnd(3, '0').slice(0, 3), 10) : 0;

    return (minutes * 60 + seconds) * 1000 + ms;
  } else if (parts.length === 1) {
    // SS or SS.ms format
    const secondsParts = parts[0].split('.');
    const seconds = parseInt(secondsParts[0], 10);
    const ms = secondsParts[1] ? parseInt(secondsParts[1].padEnd(3, '0').slice(0, 3), 10) : 0;

    return seconds * 1000 + ms;
  }

  return NaN;
};

/**
 * Find the best angle to display at a given timeline time
 * Returns the first angle that has footage, prioritizing the current angle
 *
 * @param timelineTimeMs - Current timeline position
 * @param angles - Available angles
 * @param currentAngleKey - Currently selected angle key (to prioritize)
 * @returns The best angle to display, or null if none have footage
 */
export const findBestAngle = (
  timelineTimeMs: number,
  angles: MulticamAngle[],
  currentAngleKey?: string
): MulticamAngle | null => {
  // First, try the current angle
  if (currentAngleKey) {
    const currentAngle = angles.find(a => a.angleKey === currentAngleKey);
    if (currentAngle && angleHasFootage(timelineTimeMs, currentAngle)) {
      return currentAngle;
    }
  }

  // Otherwise, find the first angle with footage
  for (const angle of angles) {
    if (angleHasFootage(timelineTimeMs, angle)) {
      return angle;
    }
  }

  return null;
};

/**
 * Get angles that have footage at a specific timeline time
 *
 * @param timelineTimeMs - Timeline position to check
 * @param angles - Available angles
 * @returns Array of angles that have footage at this time
 */
export const getAvailableAngles = (
  timelineTimeMs: number,
  angles: MulticamAngle[]
): MulticamAngle[] => {
  return angles.filter(angle => angleHasFootage(timelineTimeMs, angle));
};
